import type { StatFormat } from '@perplexicon/api-client';

import { formatDurationMs } from '../../lib/format-duration.js';

/**
 * Renders a game-declared statistic value generically from its
 * `statisticDescriptors`-declared `format` (APP_SPEC §43): the platform
 * never interprets what a game's metric *means*, only how the game itself
 * says it should be displayed.
 *
 * A `duration` metric is stored — and therefore arrives here — in
 * **milliseconds**, which is what the only producer of one emits today
 * (Word Rush's `fastestSolveMs`, `packages/games/word-rush/src/scoring.ts`
 * records `solvedAtMs - startsAtMs`). Rendering is delegated to the shared
 * `formatDurationMs` helper so any other surface that has to show an elapsed
 * time reads identically. The stored value is untouched; only its
 * presentation lives here.
 */
export function formatStatValue(value: number, format: StatFormat): string {
  switch (format) {
    case 'integer':
      return Math.round(value).toLocaleString();
    case 'decimal':
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    case 'percentage':
      return `${(value * 100).toFixed(0)}%`;
    case 'duration':
      return formatDurationMs(value);
    case 'text':
    default:
      return String(value);
  }
}
